import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getIngredients,
  createIngredient,
  updateIngredient,
  deleteIngredient,
  bulkImportIngredients,
} from "@/api/admin.api";
import type { Ingredient } from "@/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Search, Plus, Pencil, Trash2, Upload } from "lucide-react";

const CATEGORIES = [
  "fruit",
  "vegetable",
  "meat",
  "seafood",
  "dairy",
  "grain",
  "spice",
  "oil",
  "sauce",
  "other",
];

const ALLERGENS = [
  "gluten",
  "dairy",
  "eggs",
  "nuts",
  "peanuts",
  "soy",
  "fish",
  "shellfish",
  "sesame",
  "celery",
  "mustard",
  "sulfites",
];

const ALL_LANGS = [
  { code: "tr", flag: "🇹🇷" }, { code: "en", flag: "🇬🇧" }, { code: "de", flag: "🇩🇪" },
  { code: "fr", flag: "🇫🇷" }, { code: "es", flag: "🇪🇸" }, { code: "ar", flag: "🇸🇦" },
  { code: "ru", flag: "🇷🇺" }, { code: "zh", flag: "🇨🇳" },
];

interface IngredientForm {
  code: string;
  names: Record<string, string>;
  caloriesPer100g: string;
  proteinPer100g: string;
  fatPer100g: string;
  carbsPer100g: string;
  fiberPer100g: string;
  allergens: string[];
  category: string;
}

const emptyForm: IngredientForm = {
  code: "",
  names: {},
  caloriesPer100g: "0",
  proteinPer100g: "0",
  fatPer100g: "0",
  carbsPer100g: "0",
  fiberPer100g: "0",
  allergens: [],
  category: "other",
};

function formToPayload(form: IngredientForm) {
  return {
    code: form.code,
    names: form.names,
    caloriesPer100g: Number(form.caloriesPer100g),
    proteinPer100g: Number(form.proteinPer100g),
    fatPer100g: Number(form.fatPer100g),
    carbsPer100g: Number(form.carbsPer100g),
    fiberPer100g: Number(form.fiberPer100g),
    allergens: form.allergens,
    category: form.category,
  };
}

function ingredientToForm(ing: Ingredient): IngredientForm {
  return {
    code: ing.code,
    names: ing.names || {},
    caloriesPer100g: String(ing.caloriesPer100g),
    proteinPer100g: String(ing.proteinPer100g),
    fatPer100g: String(ing.fatPer100g),
    carbsPer100g: String(ing.carbsPer100g),
    fiberPer100g: String(ing.fiberPer100g),
    allergens: ing.allergens,
    category: ing.category,
  };
}

export function IngredientsPage() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<IngredientForm>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ added: number; skipped: number; total: number } | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkFileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "ingredients", search, categoryFilter],
    queryFn: () =>
      getIngredients({
        search: search || undefined,
        category: categoryFilter || undefined,
      }),
  });

  const createMutation = useMutation({
    mutationFn: createIngredient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "ingredients"] });
      setAddOpen(false);
      setForm(emptyForm);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: string; data: Partial<Ingredient> }) =>
      updateIngredient(id, d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "ingredients"] });
      setEditOpen(false);
      setEditId(null);
      setForm(emptyForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteIngredient,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "ingredients"] }),
  });

  const handleAdd = () => {
    createMutation.mutate(formToPayload(form));
  };

  const handleEdit = () => {
    if (editId) {
      updateMutation.mutate({ id: editId, data: formToPayload(form) });
    }
  };

  const openEdit = (ing: Ingredient) => {
    setEditId(ing._id);
    setForm(ingredientToForm(ing));
    setEditOpen(true);
  };

  const toggleAllergen = (allergen: string) => {
    setForm((prev) => ({
      ...prev,
      allergens: prev.allergens.includes(allergen)
        ? prev.allergens.filter((a) => a !== allergen)
        : [...prev.allergens, allergen],
    }));
  };

  const renderForm = (onSubmit: () => void, isPending: boolean) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Kod</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label>Kategori</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v ?? "other" })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Isimler</Label>
        <div className="mt-1 rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {ALL_LANGS.map((l) => (
                <tr key={l.code} className="border-b last:border-0">
                  <td className="px-3 py-1.5 text-center text-lg w-10">{l.flag}</td>
                  <td className="px-2 py-1.5">
                    <Input
                      className="h-8 text-sm"
                      placeholder={l.code.toUpperCase()}
                      value={form.names[l.code] || ""}
                      onChange={(e) => setForm({ ...form, names: { ...form.names, [l.code]: e.target.value } })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Kalori</Label>
          <Input type="number" value={form.caloriesPer100g} onChange={(e) => setForm({ ...form, caloriesPer100g: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Protein</Label>
          <Input type="number" value={form.proteinPer100g} onChange={(e) => setForm({ ...form, proteinPer100g: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Yag</Label>
          <Input type="number" value={form.fatPer100g} onChange={(e) => setForm({ ...form, fatPer100g: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Karb</Label>
          <Input type="number" value={form.carbsPer100g} onChange={(e) => setForm({ ...form, carbsPer100g: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Lif</Label>
          <Input type="number" value={form.fiberPer100g} onChange={(e) => setForm({ ...form, fiberPer100g: e.target.value })} />
        </div>
      </div>

      <div className="space-y-1">
        <Label>Alerjenler</Label>
        <div className="flex flex-wrap gap-1.5">
          {ALLERGENS.map((a) => (
            <Badge
              key={a}
              variant={form.allergens.includes(a) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => toggleAllergen(a)}
            >
              {a}
            </Badge>
          ))}
        </div>
      </div>

      <Button onClick={onSubmit} className="w-full" disabled={isPending}>
        {isPending ? "Kaydediliyor..." : "Kaydet"}
      </Button>
    </div>
  );

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Malzemeler</h1>
        <div className="flex gap-2">
        <Button variant="outline" onClick={() => { setBulkOpen(true); setBulkResult(null); }}>
          <Upload className="mr-1 size-4" />
          Toplu Ekle
        </Button>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger
            render={
              <Button
                className="gap-2"
                onClick={() => {
                  setForm(emptyForm);
                  setAddOpen(true);
                }}
              >
                <Plus className="size-4" />
                Malzeme Ekle
              </Button>
            }
          >
            {null}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Malzeme</DialogTitle>
            </DialogHeader>
            {renderForm(handleAdd, createMutation.isPending)}
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Toplu Malzeme Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                JSON dosyasi yukleyin. Dosya, malzeme dizisi icermeli. Ayni koda sahip malzemeler atlanir.
              </p>
              <input
                ref={bulkFileRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setBulkLoading(true);
                  setBulkResult(null);
                  try {
                    const text = await file.text();
                    let parsed = JSON.parse(text);
                    if (!Array.isArray(parsed)) parsed = [parsed];
                    const result = await bulkImportIngredients(parsed);
                    setBulkResult(result);
                    queryClient.invalidateQueries({ queryKey: ["admin", "ingredients"] });
                  } catch (err) {
                    setBulkResult({ added: 0, skipped: 0, total: 0 });
                    alert((err as Error).message || "JSON parse hatasi");
                  } finally {
                    setBulkLoading(false);
                    e.target.value = "";
                  }
                }}
              />
              <Button
                className="w-full"
                variant="outline"
                onClick={() => bulkFileRef.current?.click()}
                disabled={bulkLoading}
              >
                {bulkLoading ? (
                  <><svg className="mr-2 size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Yukleniyor...</>
                ) : (
                  <><Upload className="mr-2 size-4" />JSON Dosyasi Sec</>
                )}
              </Button>
              {bulkResult && (
                <div className="rounded-lg border p-3 text-sm">
                  <p><strong>Toplam:</strong> {bulkResult.total}</p>
                  <p className="text-green-600"><strong>Eklenen:</strong> {bulkResult.added}</p>
                  <p className="text-muted-foreground"><strong>Atlanan:</strong> {bulkResult.skipped} (zaten mevcut)</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-4 flex gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Malzeme ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={categoryFilter}
          onValueChange={(v) => setCategoryFilter(v === "ALL" ? "" : (v ?? ""))}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tumu</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Isim (TR)</TableHead>
              <TableHead>Isim (EN)</TableHead>
              <TableHead>Diller</TableHead>
              <TableHead>Kalori</TableHead>
              <TableHead>Protein</TableHead>
              <TableHead>Yag</TableHead>
              <TableHead>Karb</TableHead>
              <TableHead>Alerjenler</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="py-8 text-center text-muted-foreground">
                  Yukleniyor...
                </TableCell>
              </TableRow>
            ) : data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="py-8 text-center text-muted-foreground">
                  Malzeme bulunamadi
                </TableCell>
              </TableRow>
            ) : (
              data?.map((ing) => (
                <TableRow key={ing._id}>
                  <TableCell className="font-medium">{ing.names.tr ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{ing.names.en ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {ALL_LANGS.filter(l => l.code !== "tr" && l.code !== "en" && ing.names[l.code]).map(l => l.flag).join(" ")}
                  </TableCell>
                  <TableCell>{ing.caloriesPer100g}</TableCell>
                  <TableCell>{ing.proteinPer100g}g</TableCell>
                  <TableCell>{ing.fatPer100g}g</TableCell>
                  <TableCell>{ing.carbsPer100g}g</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {ing.allergens.map((a) => (
                        <Badge key={a} variant="secondary" className="text-xs">
                          {a}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{ing.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(ing)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <ConfirmDialog
                        trigger={
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                            <Trash2 className="size-4" />
                          </Button>
                        }
                        title="Malzemeyi Sil"
                        description="Bu malzemeyi silmek istediginizden emin misiniz?"
                        confirmLabel="Sil"
                        onConfirm={() => deleteMutation.mutate(ing._id)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Malzeme Duzenle</DialogTitle>
          </DialogHeader>
          {renderForm(handleEdit, updateMutation.isPending)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
